import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';

const EditProject = () => {
  const { projId } = useParams(); // Extract projId from URL
  const [project, setProject] = useState({});
  const [departments, setDepartments] = useState([]);
  const [contributors, setContributors] = useState([]);
  const [availableDepartments, setAvailableDepartments] = useState([]);
  const [availableContributors, setAvailableContributors] = useState([]);
  const [availableLabs, setAvailableLabs] = useState([]); // New state for available labs
  const [newDepartmentId, setNewDepartmentId] = useState([]);
  const [newContributorId, setNewContributorId] = useState([]);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  
  // Fetch project details, departments, contributors, available departments, available contributors, and available labs
  useEffect(() => {
    if (projId) {
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${projId}`)
        .then(response => response.json())
        .then(data => setProject(data))
        .catch(error => console.error('Error fetching project details:', error));

      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-departments/${projId}`)
        .then(response => response.json())
        .then(data => setDepartments(data.departments))
        .catch(error => console.error('Error fetching project departments:', error));

      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-contributors/${projId}`)
        .then(response => response.json())
        .then(data => setContributors(data.contributors))
        .catch(error => console.error('Error fetching project contributors:', error));

      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/departments`) // Fetch available departments
        .then(response => response.json())
        .then(data => setAvailableDepartments(data))
        .catch(error => console.error('Error fetching available departments:', error));

      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/users`) // Fetch available contributors
        .then(response => response.json())
        .then(data => setAvailableContributors(data))
        .catch(error => console.error('Error fetching available contributors:', error));

      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/researchlabs`) // Fetch available labs
        .then(response => response.json())
        .then(data => setAvailableLabs(data))
        .catch(error => console.error('Error fetching available labs:', error));
    }
  }, [projId]);


  


  // Function to update project details
  const updateProject = (event) => {
    event.preventDefault();
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/projects/${projId}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(project),
    })
      .then(response => {
        if (response.ok) {
          setSuccessMessage('Project updated successfully!');
          setErrorMessage('');
        } else {
          return response.text().then(text => { throw new Error(text); });
        }
      })
      .catch(error => {
        setErrorMessage('Failed to update project: ' + error.message);
        setSuccessMessage('');
      });
  };

  const addDepartment = (event) => {
    event.preventDefault();
  
    if (newDepartmentId) { // Ensure a department is selected
      // Fetch to add department
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-departments`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projId: projId,
          departments: [{ deptId: newDepartmentId }], // Add one department
        }),
      })
      .then(response => {
        if (response.ok) {
          setSuccessMessage('Department added successfully!');
          setErrorMessage('');
          
          // Find the department in availableDepartments
          const addedDepartment = availableDepartments.find(dept => dept.deptId === newDepartmentId);
  
          if (addedDepartment) {
            // Check if it's already in the state to prevent duplicates
            const isExisting = departments.some(dept => dept.deptId === addedDepartment.deptId);
  
            // If not already present, append it to the current list
            if (!isExisting) {
              setDepartments(prevDepartments => {
                const updatedDepartments = [...prevDepartments, addedDepartment];
                return updatedDepartments; // Return new array with appended department
              });
            } else {
              setErrorMessage('This department is already assigned to the project.');
            }
          }
          
          // Clear the selected department
          setNewDepartmentId('');
        } else {
          return response.text().then(text => { throw new Error(text); });
        }
      })
      .catch(error => {
        setErrorMessage('Failed to add department: ' + error.message);
        setSuccessMessage('');
      });
    }
  };
  
  
  // Function to add a contributor (only one at a time)
const addContributor = (event) => {
    event.preventDefault();
  
    if (newContributorId) { // Ensure a contributor is selected
      // Fetch to add contributor
      fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-contributors`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projId: projId,
          contributors: [{ userId: newContributorId }], // Add one contributor
        }),
      })
      .then(response => {
        if (response.ok) {
          setSuccessMessage('Contributor added successfully!');
          setErrorMessage('');
          
          // Find the contributor in availableContributors
          const addedContributor = availableContributors.find(user => user.userId === newContributorId);
  
          if (addedContributor) {
            // Check if the contributor is already in the state to prevent duplicates
            const isExisting = contributors.some(user => user.userId === addedContributor.userId);
  
            // If not already present, append to the current list
            if (!isExisting) {
              setContributors(prevContributors => {
                const updatedContributors = [...prevContributors, addedContributor];
                return updatedContributors; // Return new array with appended contributor
              });
            } else {
              setErrorMessage('This contributor is already assigned to the project.');
            }
          }
          
          // Clear the selected contributor
          setNewContributorId('');
        } else {
          return response.text().then(text => { throw new Error(text); });
        }
      })
      .catch(error => {
        setErrorMessage('Failed to add contributor: ' + error.message);
        setSuccessMessage('');
      });
    }
  };
  
  const handleDepartmentSelection = (e) => {
    setNewDepartmentId(e.target.value); // Store only a single selected value
  };
  
  const handleContributorSelection = (e) => {
    setNewContributorId(e.target.value); // Store only a single selected value
  };
  
  

  // Delete department
  const deleteDepartment = (deptId) => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-departments/${projId}/departments/${deptId}`, {
      method: 'DELETE',
    })
      .then(response => {
        if (response.ok) {
          setSuccessMessage('Department deleted successfully!');
          setDepartments(departments.filter(dept => dept.deptId !== deptId));
        } else {
          return response.text().then(text => { throw new Error(text); });
        }
      })
      .catch(error => {
        setErrorMessage('Failed to delete department: ' + error.message);
      });
  };

  // Delete contributor
  const deleteContributor = (userId) => {
    fetch(`${process.env.REACT_APP_API_BASE_URL}/api/project-contributors/${projId}/contributors/${userId}`, {
      method: 'DELETE',
    })
      .then(response => {
        if (response.ok) {
          setSuccessMessage('Contributor deleted successfully!');
          setContributors(contributors.filter(contributor => contributor.userId !== userId));
        } else {
          return response.text().then(text => { throw new Error(text); });
        }
      })
      .catch(error => {
        setErrorMessage('Failed to delete contributor: ' + error.message);
      });
  };

  

  return (
    <div className="container mt-5">
      <h5>Edit Project Details for {projId}</h5>
      <form onSubmit={updateProject}>
    <div className="row mb-3">
      <div className="col-md-6">
        <label className="form-label">
          <strong>Title:</strong>
        </label>
        <input
          type="text"
          className="form-control"
          value={project.projTitle || ''}
          onChange={(e) => setProject({ ...project, projTitle: e.target.value })}
        />
      </div>
      <div className="col-md-6">
        <label className="form-label">
          <strong>Summary:</strong>
        </label>
        <input
          type="text"
          className="form-control"
          value={project.projSummary || ''}
          onChange={(e) => setProject({ ...project, projSummary: e.target.value })}
        />
      </div>
    </div>

    <div className="row mb-3">
    <div className="col-md-6">
  <label className="form-label">
    <strong>Type:</strong>
  </label>
  <select
    className="form-select"
    value={project.projType || ''}
    onChange={(e) => setProject({ ...project, projType: e.target.value })}
    required
  >
    <option value="">Select a project type</option>
    <option value="Externally Funded">Externally Funded</option>
                            <option value="Internally Funded">Internally Funded</option>
                            <option value="Consultancy">Consultancy</option>
                            <option value="Funded Programme">Funded Programme</option>
                           
  </select>
</div>

      <div className="col-md-6">
        <label className="form-label">
          <strong>Utilization:</strong>
        </label>
        <input
          type="text"
          className="form-control"
          value={project.projUtilization || ''}
          onChange={(e) => setProject({ ...project, projUtilization: e.target.value })}
        />
      </div>
    </div>

    <div className="row mb-3">
      <div className="col-md-6">
        <label className="form-label">
          <strong>Initial Fund:</strong>
        </label>
        <input
          type="text"
          className="form-control"
          value={project.projInitialFund || ''}
          onChange={(e) => setProject({ ...project, projInitialFund: e.target.value })}
        />
      </div>
      {/*<div className="col-md-6">
        <label className="form-label">
          <strong>Document Upload:</strong>
        </label>
        <input
          type="text"
          className="form-control"
          value={project.projDocUpload || ''}
          onChange={(e) => setProject({ ...project, projDocUpload: e.target.value })}
        />
      </div>*/}
    </div>

    <div className="row mb-3">
    <div className="col-md-6">
  <label className="form-label">
    TRL Level:
  </label>
  <select
    className="form-select"
    name="trlLevel"
    value={project.trlLevel || ''}
    onChange={(e) => setProject({ ...project, trlLevel: e.target.value })}
    required
  >
    <option value="">Select TRL Level</option>
    <option value="1">1 - Basic Principles Observed</option>
    <option value="2">2 - Technology Concept Formulated</option>
    <option value="3">3 - Experimental Proof of Concept</option>
    <option value="4">4 - Technology Validated in Lab</option>
    <option value="5">5 - Technology Validated in Relevant Environment</option>
    <option value="6">6 - System Prototype Demonstrated in Relevant Environment</option>
    <option value="7">7 - System Prototype Demonstrated in Operational Environment</option>
    <option value="8">8 - System Completed and Qualified</option>
    <option value="9">9 - Actual System Proven in Operational Environment</option>
  </select>
</div>
      <div className="col-md-6">
        <label className="form-label">
          <strong>Research Lab:</strong>
        </label>
        <select
          className="form-select"
          value={project.labId || ''}
          onChange={(e) => setProject({ ...project, labId: e.target.value })}
        >
          <option value="">Select Research Lab</option>
          {availableLabs.map((lab) => (
            <option key={lab.labId} value={lab.labId}>{lab.labName}</option>
          ))}
        </select>
      </div>
    </div>

    

    <div className="row mb-3">
      {/*<div className="col-md-6">
        <label className="form-label">
          <strong>Keyword:</strong>
        </label>
        <input
          type="text"
          className="form-control"
          value={project.projKeyword || ''}
          onChange={(e) => setProject({ ...project, projKeyword: e.target.value })}

        />
      </div>*/}
      


      <div className="col-md-6">
        <label className="form-label">
          <strong>Start Date:</strong>
        </label>
        <input
          type="date"
          className="form-control"
          value={project.projStartDate || ''}
          onChange={(e) => setProject({ ...project, projStartDate: e.target.value })}
        />
      </div>
    </div>

    <div className="row mb-3">
      <div className="col-md-6">
        <label className="form-label">
          <strong>End Date:</strong>
        </label>
        <input
          type="date"
          className="form-control"
          value={project.projEndDate || ''}
          onChange={(e) => setProject({ ...project, projEndDate: e.target.value })}
        />
      </div>
    </div>

        
        <button type="submit" className="btn btn-primary">Update Project</button>
        {successMessage && <div className="alert alert-success mt-3">{successMessage}</div>}
        {errorMessage && <div className="alert alert-danger mt-3">{errorMessage}</div>}
      </form>

      <h5 className="mt-5">Manage Departments</h5>
      <form onSubmit={addDepartment}>
  <select
    className="form-select mb-3"
    value={newDepartmentId} // Single selection
    onChange={handleDepartmentSelection}
  >
    <option value="">Select Department to Add</option>
    {availableDepartments.map((dept) => (
      <option key={dept.deptId} value={dept.deptId}>{dept.deptName}</option>
    ))}
  </select>
  <button type="submit" className="btn btn-success">Add Department</button>
</form>
    <ul className="list-group mt-3">
      {departments.map((dept) => (
        <li key={dept.deptId} className="list-group-item d-flex justify-content-between align-items-center">
          {dept.deptName}
          <button className="btn btn-danger ms-2 rounded-3" onClick={() => deleteDepartment(dept.deptId)}>Delete</button>
        </li>
      ))}
    </ul>

    <h5 className="mt-5">Manage Contributors</h5>
    <form onSubmit={addContributor}>
  <select
    className="form-select mb-3"
    value={newContributorId} // Single selection
    onChange={handleContributorSelection}
  >
    <option value="">Select Contributor to Add</option>
    {availableContributors.map((user) => (
      <option key={user.userId} value={user.userId}>{user.username}</option>
    ))}
  </select>
  <button type="submit" className="btn btn-success">Add Contributor</button>
</form>
    <ul className="list-group mt-3">
      {contributors.map((contributor) => (
        <li key={contributor.userId} className="list-group-item d-flex justify-content-between align-items-center">
          {contributor.username}
          <button className="btn btn-danger ms-2 rounded-3" onClick={() => deleteContributor(contributor.userId)}>Delete</button>
        </li>
      ))}
    </ul>

  </div>
);
};

export default EditProject;
